import { X, Flag, Circle } from "lucide-react";
import { createPortal } from "react-dom";
import { playerBreakdown, initials, championLeaderboard } from "@/lib/scoring";

/**
 * Props:
 *  - open, onClose
 *  - event
 *  - playerId (string)
 *  - dayId (optional; if provided, shows only that day; else all finalized+in-progress days)
 */
export default function PlayerCardModal({ open, onClose, event, playerId, dayId = null }) {
  if (!open || !playerId) return null;
  const player = event.players.find((p) => p.id === playerId);
  if (!player) return null;

  const sections = playerBreakdown(event, playerId, { onlyDayId: dayId });

  // Trip totals
  const tripTotals = sections.reduce((acc, s) => {
    acc.strokes += s.totals.strokes;
    acc.points += s.totals.points;
    acc.holesPlayed += s.totals.holesPlayed;
    acc.fhHit += s.totals.fhHit;
    acc.fhAttempts += s.totals.fhAttempts;
    acc.girHit += s.totals.girHit;
    acc.girHoles += s.totals.girHoles;
    acc.putts += s.totals.putts;
    acc.puttHoles += s.totals.puttHoles;
    return acc;
  }, { strokes: 0, points: 0, holesPlayed: 0, fhHit: 0, fhAttempts: 0, girHit: 0, girHoles: 0, putts: 0, puttHoles: 0 });

  // Trip-wide champion row (day wins) — only meaningful when viewing multi-day
  let dayWins = 0;
  if (!dayId) {
    const champRow = championLeaderboard(event).find(r => r.player.id === playerId);
    if (champRow) dayWins = champRow.dayWins;
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="player-card-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[color:var(--bg-dark-green)] border border-[color:var(--border-brass)] rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col tga-fade">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-brass-light)] gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="avatar avatar-dark" style={{ width: 44, height: 44, fontSize: 18 }}>{initials(player.name)}</div>
            <div className="min-w-0">
              <div className="font-display text-xl text-[color:var(--brass-bright)] truncate">{player.name}</div>
              <div className="text-xs text-[color:var(--muted-green)]">
                HCP {player.handicap} · {sections.length} {dayId ? "day" : "day(s)"} on card
                {dayWins > 0 && <span className="ml-2 text-[color:var(--brass-bright)]">🏆 {dayWins} win{dayWins === 1 ? "" : "s"}</span>}
              </div>
            </div>
          </div>
          <button data-testid="player-card-close-btn" onClick={onClose} className="ghost-btn rounded-lg p-2"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {sections.length === 0 ? (
            <p className="helptext text-center py-6">No scores yet for {player.name}.</p>
          ) : (
            sections.map((sec) => (
              <div key={sec.dayId} className="tga-card p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-display text-lg leading-tight">{sec.dayLabel} <span className="text-xs opacity-70">· {sec.courseName}</span></div>
                    <div className="text-[11px] text-[color:var(--brass-dark)]">
                      {sec.finalized ? "Finalized" : "In progress"} · {sec.totals.holesPlayed}/18 holes
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">Points</div>
                    <div className="font-mono-metrics font-bold text-2xl">{sec.totals.points}</div>
                  </div>
                </div>

                {/* Two 9-hole grids */}
                {[0, 9].map((start) => (
                  <div key={start} className="overflow-x-auto mb-2">
                    <table className="w-full text-[11px] font-mono-metrics">
                      <thead>
                        <tr className="text-[color:var(--brass-dark)]">
                          <th className="text-left py-1 pr-2">{start === 0 ? "Front" : "Back"}</th>
                          {sec.holes.slice(start, start + 9).map((_, i) => (
                            <th key={i} className="text-center py-1 px-1 w-8">{start + i + 1}</th>
                          ))}
                          <th className="text-right py-1 pl-2">{start === 0 ? "Out" : "In"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Par */}
                        <tr className="opacity-70">
                          <td className="text-left pr-2">Par</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1">{h.par}</td>
                          ))}
                          <td className="text-right pl-2 font-bold">
                            {sec.holes.slice(start, start + 9).reduce((a, h) => a + h.par, 0)}
                          </td>
                        </tr>
                        {/* SI */}
                        <tr className="opacity-50">
                          <td className="text-left pr-2 text-[10px]">SI</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1 text-[10px]">{h.si}</td>
                          ))}
                          <td></td>
                        </tr>
                        {/* Strokes */}
                        <tr>
                          <td className="text-left pr-2 font-medium">Str</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => {
                            const diff = h.strokes ? h.strokes - h.par : null;
                            let cls = "text-center px-1";
                            if (diff !== null) {
                              if (diff <= -2) cls += " bg-[rgba(230,202,101,0.35)] rounded font-bold";      // eagle+
                              else if (diff === -1) cls += " bg-[rgba(230,202,101,0.18)] rounded font-bold"; // birdie
                              else if (diff === 0) cls += " font-bold";                                       // par
                              else if (diff === 1) cls += " opacity-80";                                      // bogey
                              else cls += " opacity-60";                                                       // worse
                            }
                            return <td key={i} className={cls}>{h.strokes || "—"}</td>;
                          })}
                          <td className="text-right pl-2 font-bold">
                            {sec.holes.slice(start, start + 9).reduce((a, h) => a + (h.strokes || 0), 0) || "—"}
                          </td>
                        </tr>
                        {/* Points */}
                        <tr>
                          <td className="text-left pr-2 text-[color:var(--brass-dark)]">Pts</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1">{h.strokes ? h.points : "—"}</td>
                          ))}
                          <td className="text-right pl-2 font-bold text-[color:var(--brass-dark)]">
                            {sec.holes.slice(start, start + 9).reduce((a, h) => a + h.points, 0)}
                          </td>
                        </tr>
                        {/* Fairway */}
                        <tr className="opacity-80">
                          <td className="text-left pr-2 text-[10px] uppercase">FH</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1">
                              {h.par === 3 ? <span className="opacity-30">·</span> : h.fhMarked ? (h.fh ? "✓" : "✗") : "—"}
                            </td>
                          ))}
                          <td className="text-right pl-2">
                            {sec.holes.slice(start, start + 9).filter(h => h.par >= 4 && h.fhMarked).length
                              ? `${sec.holes.slice(start, start + 9).filter(h => h.par >= 4 && h.fh).length}/${sec.holes.slice(start, start + 9).filter(h => h.par >= 4 && h.fhMarked).length}`
                              : "—"}
                          </td>
                        </tr>
                        {/* GIR */}
                        <tr className="opacity-80">
                          <td className="text-left pr-2 text-[10px] uppercase">GIR</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1">
                              {h.girMarked ? (h.gir ? "✓" : "✗") : "—"}
                            </td>
                          ))}
                          <td className="text-right pl-2">
                            {sec.holes.slice(start, start + 9).filter(h => h.girMarked).length
                              ? `${sec.holes.slice(start, start + 9).filter(h => h.girMarked && h.gir).length}/${sec.holes.slice(start, start + 9).filter(h => h.girMarked).length}`
                              : "—"}
                          </td>
                        </tr>
                        {/* Putts */}
                        <tr className="opacity-80">
                          <td className="text-left pr-2 text-[10px] uppercase">Pt</td>
                          {sec.holes.slice(start, start + 9).map((h, i) => (
                            <td key={i} className="text-center px-1">{h.putts ?? "—"}</td>
                          ))}
                          <td className="text-right pl-2">
                            {sec.holes.slice(start, start + 9).filter(h => h.putts !== null).length
                              ? sec.holes.slice(start, start + 9).reduce((a, h) => a + (h.putts || 0), 0)
                              : "—"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                {/* Day totals row */}
                <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-[rgba(27,51,34,0.15)] font-mono-metrics">
                  <div>
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">Strokes</div>
                    <div className="font-bold">{sec.totals.strokes || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">FH</div>
                    <div className="font-bold">{sec.totals.fhAttempts ? `${sec.totals.fhHit}/${sec.totals.fhAttempts}` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">GIR</div>
                    <div className="font-bold">{sec.totals.holesPlayed ? `${sec.totals.girHit}/${sec.totals.holesPlayed}` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">Putts</div>
                    <div className="font-bold">
                      {sec.totals.puttHoles ? <>{sec.totals.putts} <span className="text-[10px] opacity-60">{(sec.totals.putts / sec.totals.puttHoles).toFixed(1)}</span></> : "—"}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Trip totals when multi-day view */}
          {!dayId && sections.length > 1 && (
            <div className="tga-card-dark p-3">
              <div className="font-display text-lg mb-2 text-[color:var(--brass-bright)]">Trip totals</div>
              <div className="grid grid-cols-2 gap-3 font-mono-metrics text-sm">
                <div><span className="text-[color:var(--muted-green)] text-xs uppercase mr-2">Strokes</span>{tripTotals.strokes}</div>
                <div><span className="text-[color:var(--muted-green)] text-xs uppercase mr-2">Points</span>{tripTotals.points}</div>
                <div><span className="text-[color:var(--muted-green)] text-xs uppercase mr-2">FH</span>{tripTotals.fhAttempts ? `${tripTotals.fhHit}/${tripTotals.fhAttempts}` : "—"}</div>
                <div><span className="text-[color:var(--muted-green)] text-xs uppercase mr-2">GIR</span>{tripTotals.girHoles ? `${tripTotals.girHit}/${tripTotals.girHoles}` : "—"}</div>
                <div className="col-span-2"><span className="text-[color:var(--muted-green)] text-xs uppercase mr-2">Putts</span>
                  {tripTotals.puttHoles ? <>{tripTotals.putts} <span className="opacity-70 text-xs">avg {(tripTotals.putts / tripTotals.puttHoles).toFixed(2)}</span></> : "—"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
